import { Fragment, type ReactNode, useEffect, useRef, useState } from "react"
import { useParams } from "react-router-dom"
import { api } from "../lib/api"
import { useAuth, useSession } from "../lib/auth"
import { Skeleton, useToast } from "./ui"

type Kind = "task" | "project" | "page" | "space"

// A link names an item, never a company, and every page reads as the active
// company — so a colleague's link to a task in another of your companies would
// answer "Not found". This asks which of your companies owns the item and
// switches to it before the page mounts. Anything the API will not place (not
// yours, unknown, or the request failed) renders the page as before, which
// shows its own not-found.
export function FollowCompany({ kind, param, children }: { kind: Kind; param: string; children: ReactNode }) {
  const id = useParams()[param] ?? ""
  const { company } = useSession()
  const { switchCompany } = useAuth()
  const toast = useToast()
  const [settled, setSettled] = useState<string | null>(null)
  // Read at answer time, not a dependency: the switch below changes it, and
  // that must not ask again.
  const active = useRef(company.id)
  active.current = company.id

  useEffect(() => {
    let live = true
    api
      .get<{ company: { id: string; name: string } }>(`/auth/me/locate/${kind}/${id}`)
      .then(async ({ company: owner }) => {
        if (!live || owner.id === active.current) return
        await switchCompany(owner.id)
        toast(`Switched to ${owner.name}`)
      })
      .catch(() => {})
      .finally(() => live && setSettled(id))
    return () => {
      live = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kind, id])

  if (settled !== id) return <Skeleton className="h-96" />
  // Keyed by company so a page already mounted under the old one starts over:
  // its loads depend on the id in the URL, not on who it is read as.
  return <Fragment key={company.id}>{children}</Fragment>
}
